/**
 * The panel's Context (07 Workshop, rows 2, 13, 41, 42, 52, 53 and 65): what
 * every chat in the project works from. The project's description (edited in
 * place; Save applies it from the next message), its files and the pictures
 * that are material (uploads, library copies), newest first, then what is
 * pinned from the brand (loaded on every turn, by the chat and by deployed
 * agents).
 * Generated pictures are outputs (isOutputPicture).
 *
 * Files' + uploads a file or a picture, or copies pictures in from the brand
 * library, or generates one; Pinned's + opens the pin dialog. Removing a file or a picture is permanent, so it asks in the
 * Workshop dialog and says what goes and what stays. An upload shows its real
 * progress when the upload reports it, and an honest "uploading" otherwise.
 */
import { useMemo, useRef, useState } from "react";
import { Button, Menu, MenuItem, MenuTrigger, Popover, type Key } from "react-aria-components";
import { ImagePlus, Pencil, Plus, Sparkles, Upload } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import { useGetBrands } from "@/hooks/useGetBrands";
import { useWorkshopProject, useWorkshopProjects } from "../api/useWorkshopProjects";
import { getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import { usePermissions } from "@/hooks/usePermissions";
import type { WorkshopContextItemKind } from "@/types/workshop.type";
import { MediaPickerModal } from "@/app/routes/dashboard/components/media/MediaPickerModal";
import { BRAND_MEDIA_ACCEPT, isImageFile } from "@/app/routes/dashboard/api/useBrandMedia";
import { WORKSHOP_FILE_ACCEPT } from "@/app/routes/dashboard/api/useSessionAttachments";
import { useWorkshopContextItems } from "../api/useWorkshopContextItems";
import {
  useWorkshopFileMutations,
  useWorkshopFiles,
  type WorkshopFileRow,
} from "../api/useWorkshopFiles";
import {
  newMediaPreviewId,
  useWorkshopMedia,
  useWorkshopMediaCreateTarget,
  useWorkshopMediaMutations,
  type WorkshopMediaItem,
} from "../api/useWorkshopMedia";
import { useViewerId } from "../attribution";
import { isOutputPicture } from "../workshopOutputs";
import AddContextModal from "./AddContextModal";
import { FileRow, PictureRow, PinnedRow } from "./ContextRows";
import SourcePreview, { type SourcePreviewTarget } from "./SourcePreview";
import WorkshopDialog from "./WorkshopDialog";
import WorkshopLibraryPicker from "./WorkshopLibraryPicker";
import { WsInlineError } from "./WorkshopParts";
import { usePictureActions } from "./usePictureActions";

type Props = {
  brandId: string;
  projectId: string;
  canWrite: boolean;
  /** Phone: row menus are sheets. */
  isPhone: boolean;
  /** Pins and files added this session and not looked at yet. */
  unopenedContextIds?: string[];
  unopenedMediaIds?: string[];
  onNoticeContext?: (itemId: string) => void;
  onNoticeMedia?: (mediaId: string) => void;
  /** A picture to preview (from a chat card), until handled. */
  requestedPictureId?: string | null;
  onRequestHandled?: () => void;
};

type ListRow =
  | { kind: "file"; key: string; at: string; file: WorkshopFileRow }
  | { kind: "picture"; key: string; at: string; item: WorkshopMediaItem };

export default function ContextTab({
  brandId,
  projectId,
  canWrite,
  isPhone,
  unopenedContextIds = [],
  unopenedMediaIds = [],
  onNoticeContext,
  onNoticeMedia,
  requestedPictureId = null,
  onRequestHandled,
}: Props) {
  const viewerId = useViewerId();
  // The brand library is the team's: a Guest keeps pictures in the project.
  const { isGuest } = usePermissions();
  const pins = useWorkshopContextItems(projectId);
  const filesQuery = useWorkshopFiles(projectId);
  const mediaQuery = useWorkshopMedia(projectId);
  const fileMutations = useWorkshopFileMutations(projectId);
  const media = useWorkshopMediaMutations(projectId);
  const createTarget = useWorkshopMediaCreateTarget(projectId);
  const uploadRef = useRef<HTMLInputElement>(null);

  const portal = useCoShellPortal();
  const { data: brands } = useGetBrands();
  const brandName = brands?.find((b) => b.id === brandId)?.name ?? null;
  const projectQuery = useWorkshopProject(projectId);
  const { updateProject } = useWorkshopProjects(brandId);
  const description = projectQuery.data?.project.description ?? "";
  const canEditDescription = canWrite && projectQuery.data?.project.isOwner === true;
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [preview, setPreview] = useState<SourcePreviewTarget | null>(null);
  const [removeFile, setRemoveFile] = useState<WorkshopFileRow | null>(null);
  const pictures = usePictureActions({
    brandId,
    projectId,
    canWrite,
    onRemoved: (id) => {
      if (preview?.kind === "picture" && preview.item.id === id) setPreview(null);
    },
  });
  // Uploads and library copies; generated pictures are outputs.
  const material = useMemo(
    () => (mediaQuery.data ?? []).filter((m) => !isOutputPicture(m)),
    [mediaQuery.data],
  );

  // A chat card asked for a picture: it opens by being read here, and closing
  // the preview hands the request back.
  const requested = requestedPictureId
    ? material.find(
        (m) => m.id === requestedPictureId || m.copiedFromBrandMediaId === requestedPictureId,
      )
    : undefined;
  const shownPreview: SourcePreviewTarget | null =
    preview ?? (requested ? { kind: "picture", item: requested } : null);

  const pinned = useMemo(
    () => [...pins.items].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)),
    [pins.items],
  );
  const rows = useMemo<ListRow[]>(() => {
    const list: ListRow[] = [
      ...(filesQuery.data ?? []).map((file) => ({
        kind: "file" as const,
        key: `file:${file.id}`,
        at: file.createdAt,
        file,
      })),
      ...material.map((item) => ({
        kind: "picture" as const,
        key: `picture:${item.id}`,
        at: item.linkedAt,
        item,
      })),
    ];
    return list.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  }, [filesQuery.data, material]);

  const fileCount = filesQuery.data?.length ?? 0;
  const pictureCount = material.length;
  const filesFailed = filesQuery.isError && !filesQuery.data;
  const picturesFailed = mediaQuery.isError && !mediaQuery.data;
  const listLoading = filesQuery.isLoading || mediaQuery.isLoading;

  const upload = (picked: File[]) => {
    for (const file of picked) {
      if (isImageFile(file)) media.upload.mutate({ file, previewId: newMediaPreviewId() });
      else fileMutations.upload.mutate({ file, previewId: newMediaPreviewId() });
    }
  };

  const unpin = (id: string, label: string) =>
    pins.removeItem.mutate(id, {
      onError: (error) =>
        queue.add({
          title: `Couldn't unpin “${label}”`,
          description: getAiRequestErrorMessage(error),
          variant: "error",
        }),
    });

  return (
    <div className="ws-room-context">
      <section className="ws-csec" aria-labelledby="ws-csec-description">
        <div className="ws-section-head">
          <h3 id="ws-csec-description" className="ws-section-label">
            Description
          </h3>
          <span className="ws-side-push" />
          {canEditDescription && !editingDescription ? (
            <button
              type="button"
              className="ws-side-icon ws-side-icon--sm"
              aria-label={description ? "Edit the description" : "Write the description"}
              title="Edit"
              onClick={() => {
                setDescriptionDraft(description);
                setEditingDescription(true);
              }}
            >
              <Pencil aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {editingDescription ? (
          <form
            className="ws-description-edit"
            onSubmit={(event) => {
              event.preventDefault();
              setEditingDescription(false);
              if (descriptionDraft.trim() !== description.trim()) {
                updateProject.mutate(
                  { projectId, description: descriptionDraft.trim() || null },
                  {
                    onError: (error) =>
                      queue.add({
                        title: "Couldn't save the description",
                        description: getAiRequestErrorMessage(error),
                        variant: "error",
                      }),
                  },
                );
              }
            }}
          >
            <textarea
              className="ws-description-field co-input-own-focus"
              value={descriptionDraft}
              onChange={(e) => setDescriptionDraft(e.target.value)}
              maxLength={2000}
              rows={6}
              autoFocus
              aria-label="Project description"
              placeholder="What this project is for."
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  setEditingDescription(false);
                }
              }}
            />
            <p className="ws-description-hint">Creo reads these in every chat in this project.</p>
            <div className="ws-description-actions">
              <KitButton variant="secondary" type="button" onClick={() => setEditingDescription(false)}>
                Cancel
              </KitButton>
              <KitButton type="submit">Save</KitButton>
            </div>
          </form>
        ) : description ? (
          <p className="ws-description">{description}</p>
        ) : projectQuery.isLoading ? (
          <span className="ws-skel ws-skel--line" style={{ width: "80%" }} aria-hidden="true" />
        ) : (
          <p className="ws-description ws-description--empty">
            None yet. Say what this project is for; every chat here reads it.
          </p>
        )}
      </section>

      <section className="ws-csec" aria-labelledby="ws-csec-files">
        <div className="ws-section-head">
          <h3 id="ws-csec-files" className="ws-section-label">
            Files
          </h3>
          {listLoading ? null : <span className="ws-section-count">{fileCount + pictureCount}</span>}
          <span className="ws-side-push" />
          {canWrite ? (
            <MenuTrigger>
              <Button className="ws-side-icon" aria-label="Add a file or a picture">
                <Plus aria-hidden="true" />
              </Button>
              <Popover
                placement="bottom end"
                offset={6}
                UNSTABLE_portalContainer={portal ?? undefined}
                className="ws-menu-pop"
              >
                <Menu
                  aria-label="Add to the project"
                  className="ws-menu-list"
                  onAction={(key: Key) => {
                    if (key === "upload") uploadRef.current?.click();
                    else if (key === "library") setLibraryOpen(true);
                    else if (key === "generate") setGenerateOpen(true);
                  }}
                >
                  <MenuItem id="upload" textValue="Upload a file" className="ws-menu-row">
                    <Upload className="ws-menu-row-icon" aria-hidden="true" />
                    <span className="ws-menu-row-label">Upload a file</span>
                  </MenuItem>
                  {isGuest ? null : (
                    <MenuItem id="library" textValue="From the brand library" className="ws-menu-row">
                      <ImagePlus className="ws-menu-row-icon" aria-hidden="true" />
                      <span className="ws-menu-row-label">From the brand library</span>
                    </MenuItem>
                  )}
                  <MenuItem id="generate" textValue="Generate a picture" className="ws-menu-row">
                    <Sparkles className="ws-menu-row-icon" aria-hidden="true" />
                    <span className="ws-menu-row-label">Generate a picture</span>
                  </MenuItem>
                </Menu>
              </Popover>
            </MenuTrigger>
          ) : null}
          <input
            ref={uploadRef}
            type="file"
            accept={`${WORKSHOP_FILE_ACCEPT},${BRAND_MEDIA_ACCEPT}`}
            multiple
            hidden
            data-testid="ws-context-upload"
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []);
              e.target.value = "";
              if (picked.length) upload(picked);
            }}
          />
        </div>

        {filesFailed || picturesFailed ? (
          <WsInlineError
            className="ws-inline-error--compact"
            title={
              filesFailed && picturesFailed
                ? "Couldn't load the files and pictures"
                : filesFailed
                  ? "Couldn't load the files"
                  : "Couldn't load the pictures"
            }
            line={filesFailed && picturesFailed ? undefined : "What loaded is below. Nothing was lost."}
            onRetry={() => {
              if (filesFailed) void filesQuery.refetch();
              if (picturesFailed) void mediaQuery.refetch();
            }}
          />
        ) : null}

        {listLoading ? (
          <ul className="ws-clist" aria-busy="true" aria-label="Loading files">
            {[0, 1].map((i) => (
              <li key={i} className="ws-crow ws-crow--skel" aria-hidden="true">
                <span className="ws-crow-box" />
                <span className="ws-skel ws-skel--line" style={{ width: "55%" }} />
              </li>
            ))}
          </ul>
        ) : rows.length > 0 ? (
          <ul className="ws-clist" aria-label="Files and pictures">
            {rows.map((row) =>
              row.kind === "file" ? (
                <FileRow
                  key={row.key}
                  file={row.file}
                  projectId={projectId}
                  viewerId={viewerId}
                  unread={unopenedContextIds.includes(row.file.id)}
                  canWrite={canWrite}
                  isPhone={isPhone}
                  onOpen={() => {
                    onNoticeContext?.(row.file.id);
                    setPreview({ kind: "file", file: row.file });
                  }}
                  onRetry={() => fileMutations.retry.mutate(row.file.id)}
                  onRemove={() => setRemoveFile(row.file)}
                />
              ) : (
                <PictureRow
                  key={row.key}
                  item={row.item}
                  viewerId={viewerId}
                  unread={unopenedMediaIds.includes(row.item.id)}
                  isPhone={isPhone}
                  items={pictures.menuItems(row.item)}
                  onOpen={() => {
                    onNoticeMedia?.(row.item.id);
                    setPreview({ kind: "picture", item: row.item });
                  }}
                  onAction={(id) => pictures.run(id, row.item)}
                />
              ),
            )}
          </ul>
        ) : filesFailed || picturesFailed ? null : (
          <p className="ws-cempty">Nothing here yet. Add a file, or attach one in a chat.</p>
        )}
      </section>

      <section className="ws-csec" aria-labelledby="ws-csec-pinned">
        <div className="ws-section-head">
          <h3 id="ws-csec-pinned" className="ws-section-label">
            {brandName ? `Pinned from ${brandName}` : "Pinned"}
          </h3>
          {pins.isLoading || pins.isError ? null : <span className="ws-section-count">{pinned.length}</span>}
          <span className="ws-side-push" />
          {canWrite ? (
            <button
              type="button"
              className="ws-side-icon"
              aria-label="Pin something"
              title="Pin something"
              onClick={() => setPinOpen(true)}
            >
              <Plus aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {pins.isError ? (
          <WsInlineError
            className="ws-inline-error--compact"
            title="Couldn't load what is pinned"
            onRetry={() => void pins.refetch()}
          />
        ) : pins.isLoading ? (
          <span className="ws-skel ws-skel--line" style={{ width: "60%" }} aria-hidden="true" />
        ) : pinned.length === 0 ? (
          <p className="ws-cempty">
            Nothing pinned yet. Pin a universe fact, a strategy, a signal, a trend or a link, and Creo
            works from it in every chat.
          </p>
        ) : (
          <ul className="ws-clist" aria-label="Pinned">
            {pinned.map((item) => (
              <PinnedRow
                key={item.id}
                item={item}
                viewerId={viewerId}
                unread={unopenedContextIds.includes(item.id)}
                canWrite={canWrite}
                isPhone={isPhone}
                onOpen={() => {
                  onNoticeContext?.(item.id);
                  setPreview({ kind: "context", item });
                }}
                onUnpin={() => unpin(item.id, item.label)}
              />
            ))}
          </ul>
        )}
      </section>

      <SourcePreview
        brandId={brandId}
        projectId={projectId}
        target={shownPreview}
        onClose={() => {
          setPreview(null);
          if (requested) onRequestHandled?.();
        }}
      />

      {pinOpen && canWrite ? (
        <AddContextModal
          brandId={brandId}
          onClose={() => setPinOpen(false)}
          onSubmit={(vars, done) =>
            pins.addItem.mutate(vars, {
              onSuccess: () => {
                setPinOpen(false);
                done();
              },
              onError: (error) => done(getAiRequestErrorMessage(error)),
            })
          }
        />
      ) : null}

      {canWrite && !isGuest ? (
        <WorkshopLibraryPicker
          brandId={brandId}
          projectId={projectId}
          isOpen={libraryOpen}
          onOpenChange={setLibraryOpen}
        />
      ) : null}

      {generateOpen ? (
        <MediaPickerModal
          brandId={brandId}
          isOpen
          mode="select"
          initialTab="generate"
          title="Generate a picture for this project"
          canWrite={canWrite}
          hideLibrary={isGuest}
          createTarget={createTarget}
          onSelect={(item, origin) => {
            // Generate made it in the project; a pick from the library tab
            // still has to be copied in (as the old Media tab did).
            if (origin === "library") {
              media.addFromLibrary.mutate({ item, previewId: newMediaPreviewId() });
            }
            setGenerateOpen(false);
          }}
          onClose={() => setGenerateOpen(false)}
        />
      ) : null}

      {pictures.elements}

      <WorkshopDialog
        isOpen={removeFile !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveFile(null);
        }}
        title="Remove this file?"
        description={
          removeFile
            ? `“${removeFile.fileName ?? "This file"}” leaves the project and no chat reads it any more. Replies that already used it keep what they said.`
            : undefined
        }
        consequence="This cannot be undone"
        cancelLabel="Keep file"
        confirmLabel="Remove file"
        confirmTone="danger"
        onConfirm={() => {
          if (!removeFile) return;
          const file = removeFile;
          setRemoveFile(null);
          fileMutations.remove.mutate(file.id);
        }}
      />

    </div>
  );
}

export type AddContextVars = {
  kind: WorkshopContextItemKind;
  targetId?: string;
  url?: string;
  label: string;
  note?: string;
};
